import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useVoiceCallStore } from 'state/store/chat/use-voice-call-store';
import { useVoiceCall } from '../hooks/use-voice-call';
import { getVoiceStatusLabelKey, VOICE_STATUS_LABEL_DEFAULTS } from '../types/voice-call.types';
import { VoiceCallControls } from './VoiceCallControls';
import { VoiceCaptions } from './VoiceCaptions';
import { VoiceOrb } from './VoiceOrb';

/**
 * Full-screen call UI. Only shown when the user expands from VoiceCallDockedBar.
 */
export const VoiceCallOverlay = () => {
  const { t } = useTranslation();
  const status = useVoiceCallStore((state) => state.status);
  const agentState = useVoiceCallStore((state) => state.agentState);
  const isMinimized = useVoiceCallStore((state) => state.isMinimized);
  const isMuted = useVoiceCallStore((state) => state.isMuted);
  const agentCaption = useVoiceCallStore((state) => state.agentCaption);
  const userCaption = useVoiceCallStore((state) => state.userCaption);
  const setMinimized = useVoiceCallStore((state) => state.setMinimized);
  const { hangUp, toggleMute } = useVoiceCall();

  const isOpen = status !== 'idle' && !isMinimized;

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMinimized(true);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, setMinimized]);

  if (!isOpen) return null;

  const title = t('VOICE_CALL_TITLE', { defaultValue: 'Voice call' });
  const statusKey = getVoiceStatusLabelKey(status, agentState);
  const statusLabel = t(statusKey, { defaultValue: VOICE_STATUS_LABEL_DEFAULTS[statusKey] });

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      data-testid="voice-call-overlay"
      className="fixed inset-0 z-[100] flex flex-col bg-background/95 text-foreground backdrop-blur-md"
    >
      <div className="flex items-center justify-between px-4 py-3 sm:px-6">
        <span className="text-sm font-medium text-muted-foreground">{title}</span>
        <button
          type="button"
          aria-label={t('VOICE_CALL_SHOW_CHAT', { defaultValue: 'Show chat' })}
          className="flex h-9 w-9 items-center justify-center rounded-full hover:bg-muted"
          onClick={() => setMinimized(true)}
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-10 px-4">
        <VoiceOrb status={status} agentState={agentState} />
        <p
          data-testid="voice-call-status"
          className="text-base text-muted-foreground"
          aria-live="polite"
        >
          {statusLabel}
        </p>
        <VoiceCaptions agentCaption={agentCaption} userCaption={userCaption} />
      </div>

      <div className="flex justify-center pb-10 pt-4 sm:pb-14">
        <VoiceCallControls
          isMuted={isMuted}
          onToggleMute={() => void toggleMute()}
          onMinimize={() => setMinimized(true)}
          onHangUp={() => void hangUp()}
          disabled={status === 'ending'}
        />
      </div>
    </div>,
    document.body
  );
};
